import type { PluginSidebarProps } from "@d12labs/plugins/types";
import {
	BrandSquare,
	SidebarBrand,
	SidebarNavItem,
	SidebarSection,
	SidebarSubItem,
} from "@d12labs/ui/sidebar-kit";
import { useQuery } from "@tanstack/react-query";
import { Contact, KanbanSquare, UserPlus, Users } from "lucide-react";
// Row shapes derive from the server's route defs (Phase 7) — no hand-typed Contact/Page.
import { crmApi } from "./api";

/**
 * Contextual sidebar for the CRM plugin: Workspace nav (Contacts / New contact /
 * Pipeline) plus a live "Recent contacts" list. The back chevron returns to the
 * app launcher. Mirrors the contacts-list query key so the fetch is shared.
 */
export const pluginSidebar = ({ onBack, onNavigate }: PluginSidebarProps) => {
	const contacts = useQuery({
		queryKey: ["crm", "contacts", { q: "", page: 1 }],
		queryFn: () => crmApi.listContacts({ query: { q: "", page: 1 } }),
	});

	const recent = (contacts.data?.items ?? []).slice(0, 6);

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
			<SidebarBrand
				icon={<BrandSquare icon={Contact} />}
				title="CRM"
				subtitle="Contacts & deals"
				onBack={onBack}
			/>
			<div className="flex flex-col gap-1 px-2 py-2">
				<SidebarSection label="Workspace">
					<SidebarNavItem to="/crm" exact icon={Users} label="Contacts" onNavigate={onNavigate} />
					<SidebarNavItem
						to="/crm/contacts/new"
						icon={UserPlus}
						label="New contact"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/crm/pipeline"
						icon={KanbanSquare}
						label="Pipeline"
						onNavigate={onNavigate}
					/>
				</SidebarSection>

				{recent.length > 0 ? (
					<SidebarSection label="Recent contacts">
						{recent.map((c) => (
							<SidebarSubItem
								key={c.id}
								to={`/crm/contacts/${c.id}`}
								label={c.name}
								onNavigate={onNavigate}
							/>
						))}
					</SidebarSection>
				) : null}
			</div>
		</div>
	);
};
